import { Cancel, CheckCircle, Person } from '@mui/icons-material';
import { Alert, Box, CircularProgress, Divider } from '@mui/material';
import React from 'react';
import {
  AcceptAdvisorInvitationRequest,
  AdvisorInvitationVerificationResponse,
} from '../../models/Advisor';
import { StyledButton } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import { StyledCard, StyledCardContent, StyledHeading, StyledTypography } from '../styles/styles';
import {
  CENTERED_PAGE_BOX_SX,
  getEntityTypeLabel,
  TEXT_SECONDARY_COLOR,
} from './acceptInvitationConstants';

type VerifiedAdvisorInvitation = NonNullable<AdvisorInvitationVerificationResponse['invitation']>;

type FormErrors = {
  firstName?: string;
  lastName?: string;
  mobile?: string;
};

export const AcceptInvitationLoadingView: React.FC = () => (
  <Box sx={CENTERED_PAGE_BOX_SX}>
    <Box sx={{ textAlign: 'center' }}>
      <CircularProgress size={60} />
      <StyledTypography sx={{ mt: 2 }}>Verifying invitation...</StyledTypography>
    </Box>
  </Box>
);

export const AcceptInvitationSuccessView: React.FC<{ onGoHome: () => void }> = ({ onGoHome }) => (
  <Box sx={CENTERED_PAGE_BOX_SX}>
    <StyledCard tone="tone1" sx={{ maxWidth: 500 }}>
      <StyledCardContent sx={{ py: 3, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <StyledHeading headerVariant="heading2" sx={{ mb: 2 }}>
          Invitation Accepted!
        </StyledHeading>
        <StyledTypography sx={{ mb: 3 }}>
          You will receive a verification email to complete your sign up by setting a password.
        </StyledTypography>
        <StyledTypography variant="body2" sx={{ mb: 3, color: TEXT_SECONDARY_COLOR }}>
          Please check your email and follow the instructions to set up your account.
        </StyledTypography>
        <StyledButton variant="contained" onClick={onGoHome}>
          Go to Home
        </StyledButton>
      </StyledCardContent>
    </StyledCard>
  </Box>
);

export const AcceptInvitationErrorView: React.FC<{ error: string; onGoHome: () => void }> = ({
  error,
  onGoHome,
}) => (
  <Box sx={CENTERED_PAGE_BOX_SX}>
    <StyledCard tone="tone1" sx={{ maxWidth: 500 }}>
      <StyledCardContent sx={{ py: 3, textAlign: 'center' }}>
        <Cancel sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        <StyledHeading headerVariant="heading2" sx={{ mb: 2 }}>
          Invalid Invitation
        </StyledHeading>
        <StyledTypography sx={{ mb: 3 }}>{error}</StyledTypography>
        <StyledButton variant="contained" onClick={onGoHome}>
          Go to Home
        </StyledButton>
      </StyledCardContent>
    </StyledCard>
  </Box>
);

type AcceptInvitationFormViewProps = {
  invitation: VerifiedAdvisorInvitation;
  isExistingContact: boolean;
  formData: AcceptAdvisorInvitationRequest;
  formErrors: FormErrors;
  error: string | null;
  submitting: boolean;
  isAcceptDisabled: boolean;
  onInputChange: (field: keyof AcceptAdvisorInvitationRequest, value: string) => void;
  onAccept: () => void;
  onReject: () => void;
};

const AcceptInvitationDetailsSection: React.FC<{ invitation: VerifiedAdvisorInvitation }> = ({
  invitation,
}) => (
  <Box>
    <StyledTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
      Invitation Details
    </StyledTypography>

    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Person sx={{ mr: 1, color: TEXT_SECONDARY_COLOR }} />
      <StyledTypography>
        <strong>Invited by:</strong> {invitation.inviterName}
      </StyledTypography>
    </Box>

    {invitation.entities && invitation.entities.length > 0 && (
      <Box sx={{ mb: 2 }}>
        <StyledTypography sx={{ mb: 1, fontWeight: 600 }}>Investment Entities:</StyledTypography>
        {invitation.entities.map((entity) => (
          <StyledTypography key={entity.id} variant="body2" sx={{ ml: 2 }}>
            • {entity.name} ({getEntityTypeLabel(entity.type)})
          </StyledTypography>
        ))}
      </Box>
    )}
  </Box>
);

const AcceptInvitationProfileSection: React.FC<{
  isExistingContact: boolean;
  formData: AcceptAdvisorInvitationRequest;
  formErrors: FormErrors;
  error: string | null;
  onInputChange: (field: keyof AcceptAdvisorInvitationRequest, value: string) => void;
}> = ({ isExistingContact, formData, formErrors, error, onInputChange }) => (
  <Box sx={{ mb: 4 }}>
    <StyledTypography sx={{ mb: 2, fontWeight: 600 }}>
      {isExistingContact ? 'Accept Invitation' : 'Complete Your Profile'}
    </StyledTypography>

    {isExistingContact ? (
      <Alert severity="info" sx={{ mb: 3 }}>
        <StyledTypography sx={{ mb: 1 }}>
          You already have an account. The details you used during signup will be used for this
          invitation.
        </StyledTypography>
        <StyledTypography>
          You can update your details at any time by logging in and going to Settings.
        </StyledTypography>
      </Alert>
    ) : (
      <StyledTypography sx={{ mb: 3, color: TEXT_SECONDARY_COLOR }}>
        Please provide your details to complete the invitation acceptance:
      </StyledTypography>
    )}

    {error && (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    )}

    {!isExistingContact && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <StyledInput
            label="First Name"
            value={formData.firstName}
            onChange={(e) => onInputChange('firstName', e.target.value)}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            fullWidth
            required
          />
          <StyledInput
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => onInputChange('lastName', e.target.value)}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            fullWidth
            required
          />
        </Box>

        <StyledInput
          label="Mobile Number"
          value={formData.mobile}
          onChange={(e) => onInputChange('mobile', e.target.value)}
          error={!!formErrors.mobile}
          helperText={formErrors.mobile}
          placeholder="Mobile number"
          fullWidth
          required
        />
      </Box>
    )}
  </Box>
);

export const AcceptInvitationFormView: React.FC<AcceptInvitationFormViewProps> = ({
  invitation,
  isExistingContact,
  formData,
  formErrors,
  error,
  submitting,
  isAcceptDisabled,
  onInputChange,
  onAccept,
  onReject,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      p: 2,
    }}
  >
    <StyledCard tone="tone1" sx={{ maxWidth: 600, width: '100%' }}>
      <StyledCardContent sx={{ py: 4 }}>
        <StyledHeading headerVariant="heading2" sx={{ mb: 3, textAlign: 'center' }}>
          Advisor Invitation
        </StyledHeading>

        <AcceptInvitationDetailsSection invitation={invitation} />
        <Divider sx={{ my: 3 }} />
        <AcceptInvitationProfileSection
          isExistingContact={isExistingContact}
          formData={formData}
          formErrors={formErrors}
          error={error}
          onInputChange={onInputChange}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <StyledButton variant="outlined" onClick={onReject} disabled={submitting} fullWidth>
            Decline Invitation
          </StyledButton>
          <StyledButton
            variant="contained"
            onClick={onAccept}
            disabled={isAcceptDisabled}
            fullWidth
          >
            {submitting ? <CircularProgress size={20} /> : 'Accept Invitation'}
          </StyledButton>
        </Box>
      </StyledCardContent>
    </StyledCard>
  </Box>
);
